import { XMLParser } from 'fast-xml-parser'
import { audioAdClient } from '~/server/utils/fetchClient'

interface VastParams {
  contentId: string
  contentName: string
  contentLanguage: string | null
  contentEpisode: string
  apiKey?: string
}

interface Triggers {
  impression: string
  start: string
  firstQuartile: string
  midpoint: string
  thirdQuartile: string
  complete: string
}

interface VastResponse {
  source: string
  triggers: Triggers
}

export const fetchAudioAd = async (params: VastParams): Promise<VastResponse | null> => {
  const runtimeConfig = useRuntimeConfig()

  params.apiKey = runtimeConfig.public.audioAdKey

  const queryParams = new URLSearchParams(Object.entries(params))

  const xmlResponse = await audioAdClient<string>('vast/4.1?' + queryParams.toString())

  // Define options for the parser
  const options = {
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
  }

  // Create a parser instance with the options
  const parser = new XMLParser(options)
  const parsedData = parser.parse(xmlResponse)

  if (!parsedData.VAST?.Ad) {
    return null
  }

  const ad = Array.isArray(parsedData.VAST.Ad) ? parsedData.VAST.Ad[0] : parsedData.VAST.Ad

  // Extract necessary data
  const source: string = ad.InLine.Creatives.Creative.Linear.MediaFiles.MediaFile['#text']
  // console.log(ad.InLine.Creatives.Creative.Linear.TrackingEvents.Tracking)

  const triggers: Triggers = {
    impression: ad.InLine.Impression['#text'],
    start: ad.InLine.Creatives.Creative.Linear.TrackingEvents.Tracking.find(
      (t: { '#text': string; '@_event': string }) => t['@_event'] === 'start'
    )['#text'],
    firstQuartile: ad.InLine.Creatives.Creative.Linear.TrackingEvents.Tracking.find(
      (t: { '#text': string; '@_event': string }) => t['@_event'] === 'firstQuartile'
    )['#text'],
    midpoint: ad.InLine.Creatives.Creative.Linear.TrackingEvents.Tracking.find(
      (t: { '#text': string; '@_event': string }) => t['@_event'] === 'midpoint'
    )['#text'],
    thirdQuartile: ad.InLine.Creatives.Creative.Linear.TrackingEvents.Tracking.find(
      (t: { '#text': string; '@_event': string }) => t['@_event'] === 'thirdQuartile'
    )['#text'],
    complete: ad.InLine.Creatives.Creative.Linear.TrackingEvents.Tracking.find(
      (t: { '#text': string; '@_event': string }) => t['@_event'] === 'complete'
    )['#text'],
  }

  return {
    source,
    triggers,
  }
}
